import { user } from 'Cloud/Application/User';
import { logger } from 'lib/logger';
import { OverquotaNewAPICall } from 'reactApp/api/overquota/OverquotaNewAPICall';
import { OverquotaRestoreRequestAPICall } from 'reactApp/api/overquota/OverquotaRestoreRequestAPICall';
import { OverquotaUnblockAPICall } from 'reactApp/api/overquota/OverquotaUnblockAPICall';
import { ProfileAgreeLaApiCall } from 'reactApp/api/profile/ProfileAgreeLa';
import { ProfileFullApiCall } from 'reactApp/api/profile/ProfileFullAPICall';
import { ProfileUserLaApiCall } from 'reactApp/api/profile/ProfileUserLa';
import { UserNewDeviceAPICall } from 'reactApp/api/profile/UserNewDeviceAPICall';
import { ANONYM_USER, IS_MOBILE_BROWSER, IS_PHONE_BROWSER, IS_PUBLIC, X_PAGE_ID } from 'reactApp/appHelpers/configHelpers';
import { onboardingStepsEnabled } from 'reactApp/appHelpers/featuresHelpers/features/onboardingSteps';
import { renderUpdateLicenseDialog } from 'reactApp/components/BaseConfirmDialog/BaseConfirmDialog.helpers';
import { renderCreateDocumentByQueryActionDialog } from 'reactApp/components/CreateDocumentByLinkDialog/createDocumentByLinkDialog.helpers';
import { ROOT_FOLDER_ID } from 'reactApp/constants/magicIdentificators';
import { DEVICE_ID_WEB } from 'reactApp/modules/profile/profile.contants';
import {
    agreeUpdateLicenseRequest,
    checkUpdateLicenseCompleted,
    checkUpdateLicenseRequest,
    requestDataRestore,
    resetProfileOverquotaState,
    setClearedUserProcessed,
    setProfileOverquotaStateFailure,
    setProfileOverquotaStateStart,
    setProfileOverquotaStateSuccess,
    setProfileUblockUser,
} from 'reactApp/modules/profile/profile.module';
import { getUserOverquotaState } from 'reactApp/modules/profile/profile.selectors';
import { ProfileApiResponses, ProfileFlags } from 'reactApp/modules/profile/profile.types';
import { routeChangeSuccess } from 'reactApp/modules/router/router.module';
import { getCurrentRouteId, getCurrentStorage } from 'reactApp/modules/router/router.selectors';
import { showSnackbarAction } from 'reactApp/modules/snackbar/snackbar.actions';
import { SnackbarTypes } from 'reactApp/modules/snackbar/snackbar.types';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import { ViewerSelectors } from 'reactApp/modules/viewer/viewer.selectors';
import { updateVigboAccountLinkState } from 'reactApp/modules/vigbo/vigbo.module';
import { VigboAccountLinkState } from 'reactApp/modules/vigbo/vigbo.types';
import { getBooleanCookie } from 'reactApp/utils/cookie';
import { formatFileHistoryDate } from 'reactApp/utils/formatDate';
import { sendDwh, sendGa, sendKaktamLog, sendXray } from 'reactApp/utils/ga';
import { captureException } from 'reactApp/utils/tracer';
import { channel } from 'redux-saga';
import { delay, put, select, take, takeEvery, takeLatest } from 'redux-saga/effects';
import { call } from 'typed-redux-saga';

const sentMeAdsCloudCookieName = 'sent_ads_cloud';
const defaultData = { did: DEVICE_ID_WEB };

const newDeviceApiCall = () =>
    new UserNewDeviceAPICall().makeRequest({
        ...defaultData,
        name: DEVICE_ID_WEB,
        plid: DEVICE_ID_WEB,
    });

const agreeLicenseApiCall = ({ initialize_unsubscribed, first_login = '', steps_onboarding = false }) =>
    new ProfileAgreeLaApiCall().makeRequest({
        ...defaultData,
        initialize_unsubscribed,
        first_login,
        steps_onboarding,
    });

const checkLicenseApiCall = () => new ProfileUserLaApiCall().makeRequest(defaultData);

const debug = (...args) => {
    logger.verbose('[profile.saga]', ...args);
};

let isCheckUpdateDone = false;

function* handleApiError(error, retry, data = undefined, silent = false) {
    const message = error?.response?.message;
    if (message === ProfileApiResponses.USER_NOT_FOUND || message === ProfileApiResponses.DEVICE_NOT_FOUND) {
        try {
            const result = yield* call(newDeviceApiCall);
            debug('newDeviceApiCall:', result);
            yield* call(retry, data);
            return;
        } catch (error) {
            if (!silent) {
                captureException(error);
                logger.error(error);
            }
            return error;
        }
    }
    return error;
}

function* agreeUpdateLicense(action) {
    try {
        const sentMeAdsCloudCookieValue = getBooleanCookie(sentMeAdsCloudCookieName, X_PAGE_ID.toLowerCase());
        const result = yield* call(agreeLicenseApiCall, {
            initialize_unsubscribed: !sentMeAdsCloudCookieValue,
            first_login: action?.payload?.source,
            // По параметру на стороне бека определяют каким изерам отправлять на почту письмо по онбордингу
            steps_onboarding: onboardingStepsEnabled,
        });

        sendDwh({
            eventCategory: 'la',
            action: action?.payload?.isUpdate ? 'update' : 'agree',
        });

        debug('agreeLicenseApiCall:', result);
    } catch (error) {
        captureException(error);
        logger.error(error);
        yield* call(handleApiError, error, agreeUpdateLicense, action);
    } finally {
        user.setParam('updateLicenseCompleted', true);
        yield put(checkUpdateLicenseCompleted());
    }
}

function* askForUpdateLicense(forceReloadPageOnSuccess?: boolean) {
    sendGa('la-update', 'show');

    const chan = channel();

    renderUpdateLicenseDialog({
        onSuccess: () => chan.put(true),
    });

    yield take(chan);
    sendGa('la-update', 'agree');

    yield* call(renderCreateDocumentByQueryActionDialog);

    yield* call(agreeUpdateLicense, { payload: { source: 'update', isUpdate: true } });

    if (forceReloadPageOnSuccess) {
        sendXray(['overquota', 'la', 'success', 'reload'], null, { sendImmediately: true });
        yield delay(1000);
        window.location.reload();
    }
}

function* checkUpdateLicense() {
    const isNewbie = yield select(UserSelectors.isNewbie);
    const isBizUser = yield select(UserSelectors.isBizUser);
    const isViewerActive = yield select(ViewerSelectors.isViewerActive);
    const isViewerOpening = yield select(ViewerSelectors.isViewerOpening);
    const id = yield select(getCurrentRouteId);
    const storage = yield select(getCurrentStorage);

    if (
        isCheckUpdateDone ||
        isViewerActive ||
        isViewerOpening ||
        isNewbie ||
        isBizUser ||
        IS_PUBLIC ||
        IS_MOBILE_BROWSER ||
        ANONYM_USER ||
        (storage === EStorageType.attaches && id && id !== ROOT_FOLDER_ID)
    ) {
        return;
    }

    try {
        isCheckUpdateDone = true;
        const { data } = yield* call(checkLicenseApiCall);

        if (data?.status !== 'NOT_ACCEPTED') {
            yield put(checkUpdateLicenseCompleted());

            yield* call(renderCreateDocumentByQueryActionDialog);

            return;
        }

        yield* call(askForUpdateLicense);
    } catch (error) {
        yield* call(handleApiError, error, checkUpdateLicense);
        yield put(checkUpdateLicenseCompleted());
        captureException(error);
        logger.error(error);
    }
}

/**
 *  добавлен для https://jira.vk.team/browse/CLOUDWEB-18411 и к использованию в других сценарииях не рекомендуется;
 *  используйте checkUpdateLicense;
 */
function* forceCheckUpdateLicense() {
    try {
        sendXray(['overquota', 'la', 'check', 'force']);
        const { data } = yield* call(checkLicenseApiCall);

        if (data?.status !== 'NOT_ACCEPTED') {
            yield put(checkUpdateLicenseCompleted());

            yield* call(renderCreateDocumentByQueryActionDialog);

            return;
        }
        sendXray(['overquota', 'la', 'check', 'ask']);
        yield* call(askForUpdateLicense, true);
    } catch (error) {
        yield* call(handleApiError, error, checkUpdateLicense);
        yield put(checkUpdateLicenseCompleted());
        captureException(error);
        logger.error(error);
    }
}

function* loadUserFullProfileHelper() {
    try {
        yield put(setProfileOverquotaStateStart());
        const { data: profileResponse } = yield new ProfileFullApiCall().makeRequest({ did: DEVICE_ID_WEB });

        if (profileResponse?.code === 'OK') {
            const data = {
                state: profileResponse.profile?.properties?.['overquota.state'],
                blockTime: (profileResponse.profile?.properties?.['overquota.block_time'] || 0) * 1000,
                clearTime: (profileResponse.profile?.properties?.['overquota.clear_time'] || 0) * 1000,
                startPipeline: (profileResponse.profile?.properties?.['overquota.start_pipeline_time'] || 0) * 1000,
                version: profileResponse.profile?.version,
                devices: profileResponse.profile?.devices,
                ctime: profileResponse.profile?.ctime,
                flags: profileResponse.profile?.flags,
                cycleCount:
                    typeof profileResponse.profile?.properties?.['overquota.cnt'] === 'string'
                        ? parseInt(profileResponse.profile?.properties?.['overquota.cnt'])
                        : 0,
            };

            if (profileResponse.profile?.flags?.includes(ProfileFlags.VIGBO_LINKED)) {
                yield put(updateVigboAccountLinkState({ linkState: VigboAccountLinkState.linked }));
            }

            yield put(setProfileOverquotaStateSuccess(data));
        }
    } catch (error) {
        return error;
    }
}

export function* handleLoadUserFullProfile(forceLA?: boolean, forceRetryOn404?: boolean) {
    try {
        let error = yield loadUserFullProfileHelper();
        if (error && forceRetryOn404) {
            error = yield handleApiError(error, handleLoadUserFullProfile, undefined, true);
        }
        if (error) {
            throw error;
        }
    } catch (error) {
        if (forceLA) {
            yield* call(handleApiError, error, forceCheckUpdateLicense);
        }

        const isNewbie = yield select(UserSelectors.isNewbie);

        sendKaktamLog(
            {
                text: 'fullprofile api err',
                time: Date.now().toLocaleString(),
                forceLA,
                isNewbie,
                isPhone: IS_PHONE_BROWSER,
                error,
            },
            'fullprofilecloud'
        );

        yield put(setProfileOverquotaStateFailure());
        captureException(error, { issueKey: 'fullprofile/error' });
        logger.error(error);
    }
}

function* handleProfileUblockUser() {
    try {
        yield new OverquotaUnblockAPICall().makeRequest();

        yield* call(handleLoadUserFullProfile);

        const quotaState = yield select(getUserOverquotaState);

        yield put(
            showSnackbarAction({
                id: 'overquota-unblocked-message',
                type: SnackbarTypes.success,
                text: `Пространство&nbsp;разблокировано до&nbsp;${formatFileHistoryDate(
                    quotaState.clearTime
                )}&nbsp;,&nbsp;после&nbsp;файлы&nbsp;будут&nbsp;удалены`,
                closable: true,
            })
        );
    } catch (error) {
        yield put(
            showSnackbarAction({
                id: 'overquota-unblocked-message-err',
                type: SnackbarTypes.failure,
                text: `Не удалось разблокировать`,
                closable: true,
            })
        );
        captureException(error, { issueKey: 'unblock user request error' });
        logger.error(error);
    }
}

function* handleClearedUser(action: ReturnType<typeof setClearedUserProcessed>) {
    try {
        yield new OverquotaNewAPICall().makeRequest();

        yield* call(handleLoadUserFullProfile, action.payload.forceLA);

        const quotaState: ReturnType<typeof getUserOverquotaState> = yield select(getUserOverquotaState);

        if (quotaState.state === 'cleared' || quotaState.state === 'after_clear') {
            // Бек не успел обработать, потому по требованию задачи пока ставим локально сбрасываем стейт оверквоты
            yield put(resetProfileOverquotaState());
        }
    } catch (error) {
        captureException(error, { issueKey: 'overquota/new request error' });
        logger.error(error);
    }
}

function* handleRestoreRequest() {
    try {
        yield new OverquotaRestoreRequestAPICall().makeRequest();

        yield* call(handleLoadUserFullProfile, true);

        const quotaState: ReturnType<typeof getUserOverquotaState> = yield select(getUserOverquotaState);

        if (quotaState.state === 'cleared' || quotaState.state === 'after_clear') {
            // Бек не успел обработать, потому по требованию задачи пока ставим локально сами ставим такой стейт оверквоты
            yield put(setProfileOverquotaStateSuccess({ state: 'restore_requested' }));
        }
    } catch (error) {
        yield put(
            showSnackbarAction({
                type: SnackbarTypes.warning,
                text: 'Снять ограничение получится, но чуть позже&nbsp;- повторите попытку через некоторое время',
                id: 'restoreReqError',
                closable: true,
                hideCloseIcon: true,
                disableCloseTimeout: false,
            })
        );
        captureException(error, { issueKey: 'restore request error' });
        logger.error(error);
    }
}

export function* handleRestoreRequestByAction() {
    try {
        yield* call(handleLoadUserFullProfile);

        const quotaState: ReturnType<typeof getUserOverquotaState> = yield select(getUserOverquotaState);

        sendXray(['overq', 'restore', 'bylink', quotaState.state]);

        if (quotaState.state === 'after_clear') {
            yield* call(handleRestoreRequest);
        }
    } catch (error) {
        captureException(error, { issueKey: 'restore by link error' });
        logger.error(error);
    }
}

export function* watchProfile() {
    yield takeEvery(routeChangeSuccess.toString(), checkUpdateLicense);
    yield takeEvery(checkUpdateLicenseRequest.toString(), checkUpdateLicense);
    yield takeLatest(agreeUpdateLicenseRequest.toString(), agreeUpdateLicense);
    yield takeLatest(setProfileUblockUser.toString(), handleProfileUblockUser);
    yield takeLatest(setClearedUserProcessed.toString(), handleClearedUser);
    yield takeLatest(requestDataRestore.toString(), handleRestoreRequest);
}
